'use client'

import * as React from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

/**
 * A password `Field` (sistema/Field.md) with show and hide: the visible label,
 * optional help linked by `aria-describedby`, and an icon button inside the
 * control that says what it does. `labelAction` sits on the label's row
 * ("¿Te olvidaste la contraseña?").
 */
export function PasswordField({
  label,
  help,
  labelAction,
  id: idProp,
  ...inputProps
}: Omit<React.ComponentProps<'input'>, 'type' | 'className'> & {
  label: string
  help?: string
  labelAction?: React.ReactNode
}) {
  const t = useTranslations('encuentro.auth.fields')
  const autoId = React.useId()
  const id = idProp ?? autoId
  const helpId = help ? `${id}-help` : undefined
  const [visible, setVisible] = React.useState(false)

  return (
    <div data-slot="field" className="grid content-start gap-2 font-en-sans text-en-ink">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <label htmlFor={id} className="text-en-label">
          {label}
        </label>
        {labelAction}
      </div>
      {help && (
        <p id={helpId} className="text-en-meta text-en-ink-muted">
          {help}
        </p>
      )}
      <div className="relative">
        <Input
          look="encuentro"
          {...inputProps}
          id={id}
          type={visible ? 'text' : 'password'}
          aria-describedby={helpId}
          className="pr-12"
        />
        <Button
          look="encuentro"
          variant="ghost"
          size="sm"
          iconOnly
          aria-label={visible ? t('hidePassword') : t('showPassword')}
          aria-pressed={visible}
          onClick={() => setVisible((v) => !v)}
          className="absolute top-1/2 right-1 -translate-y-1/2"
        >
          {visible ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
        </Button>
      </div>
    </div>
  )
}
